import {
  BaseBotFormValues,
  BotBulkActionData,
  BotBulkActionValidationData,
  BotFormType,
  BotFormValues,
  BulkEditBotData,
  Error,
  StrategyOrdersOptions,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import useBotAmount from "@shared/features/bots/BotForm/useBotAmount";
import {
  BotData,
  BotType,
  ExchangeToSymbolPrices,
  LongShort,
  PrecisionType,
} from "@shared/types";
import { BotAmountData } from "@shared/features/bots/types";
import { getConversionMinCost } from "@shared/features/trades/common/utils";
import { OrderTypes } from "@shared/features/trades/common/types";
import {
  getBotInitialAmount,
  getBotValues,
  getHedgingValidationOrders,
  getStrategyOrders,
  isHedgingRangeMode,
  validateBotAmount,
  validateBotStrategies,
  validateBulkStopType,
  validateCustomFirstEntry,
  validateDefaultStop,
  validateDefaultTakeProfit,
  validateGridsData,
  validateHedgingGridsData,
} from "@shared/features/bots/BotForm/common/utils";
import { useContext, useMemo } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { isSymbol } from "@shared/types/utils";
import { cloneDeep, isObject } from "lodash";
import { useClientsData, useSymbolsMetadata } from "@shared/utils/hooks/misc";
import { sortByExchangeOrder } from "@shared/utils/misc";
import { MARGIN_EXCHANGES } from "@shared/common/constants";
import {
  useDefaultExchangeSymbol,
  useValidateMarginData,
} from "@shared/features/bots/BotForm/common/hooks";

interface useValidateBotsProps {
  botsData?: BotData[];
  exchangeToSymbolPrices?: ExchangeToSymbolPrices;
}

const useValidateBots = ({
  botsData,
  exchangeToSymbolPrices,
}: useValidateBotsProps) => {
  const {
    checkedFields,
    formValues,
    botType,
    botFormType,
    accounts,
    symbols,
    useSymbolFromAlert,
    botGeneralData,
    isMarginExchange,
    marginType,
    getBulkBotCreateGeneralDataStatus,
  } = useContext(BotFormContext)!;
  const { currentData: botsBulkGeneralData } =
    getBulkBotCreateGeneralDataStatus;
  const isGridBot = botType === BotType.GRID;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isTvBots = isTradingViewSignalsBot || botType === BotType.TRADING_VIEW;
  const isBulkCreate = botFormType === BotFormType.BULK_CREATE;
  const isBulkEdit = botFormType === BotFormType.BULK_EDIT;
  const { getPrecision } = usePrecisions();
  const {
    getSymbolMetadata,
    getAmountConversionFormula,
    getQuantityConversionFormula,
    isSymbolActiveInAccount,
    isSuccess,
    isSymbolActiveInExchange,
  } = useSymbolsMetadata();
  const { getBotAmountData, status: getBotAmountDataQuery } = useBotAmount({
    botType,
    botFormType,
    botsData,
  });
  const { getAccountById } = useClientsData();
  const { getDefaultSymbol } = useDefaultExchangeSymbol();
  const { validateMarginData } = useValidateMarginData();

  const getSymbolPriceData = (
    symbol: string | undefined,
    exchange: string | undefined
  ) => {
    return symbol && exchange
      ? exchangeToSymbolPrices?.[exchange]?.[symbol]
      : undefined;
  };

  const getBotCurrentPrice = (formValues: BaseBotFormValues, bot?: BotData) => {
    const { symbols, account, exchanges } = formValues;
    let symbol = isSymbol(symbols) ? undefined : symbols?.[0];
    const parsedAccount = isSymbol(account) ? undefined : account;
    const parsedExchanges = isSymbol(exchanges) ? bot?.exchanges : exchanges;
    let exchange = parsedAccount?.exchange;
    if (!exchange && parsedExchanges) {
      const sortedExchanges = sortByExchangeOrder(parsedExchanges);
      if (symbol) {
        exchange = sortedExchanges?.find((exchange) =>
          isSymbolActiveInExchange(symbol, exchange)
        );
      } else {
        exchange = sortedExchanges?.[0];
      }
    }
    if (!symbol && isTvBots) {
      symbol = getDefaultSymbol(exchange);
    }
    return getSymbolPriceData(symbol, exchange);
  };

  const getMinAmountByPrice = (
    botAmountData: BotAmountData,
    price: number,
    leverage?: number,
    symbol?: string,
    exchange?: string
  ): number => {
    const symbolMetadata = getSymbolMetadata(symbol, exchange);
    const quantityPrecision = getPrecision(
      PrecisionType.QUANTITY,
      symbol,
      exchange
    );
    return getConversionMinCost(
      true,
      leverage || 1,
      botAmountData?.mainCoinAmountPrecision || 0,
      quantityPrecision,
      price,
      getAmountConversionFormula(symbol, exchange),
      symbolMetadata?.amount_min,
      symbolMetadata?.quantity_min,
      getQuantityConversionFormula(symbol, exchange)
    );
  };

  const getBotParsedEntries = (
    botAmountData: BotAmountData,
    entryStrategy: BaseBotFormValues["entryStrategy"],
    entriesEnabled: boolean,
    currentPrice: number,
    pricePrecision: number,
    leverage?: number,
    symbol?: string,
    exchange?: string,
    isReverse?: boolean,
    gridsData?: BaseBotFormValues["gridsData"],
    strategyOrdersOptions?: StrategyOrdersOptions
  ) => {
    return getStrategyOrders(
      botAmountData,
      currentPrice,
      pricePrecision || 0,
      !!isReverse,
      OrderTypes.ENTRY,
      entriesEnabled ? entryStrategy : undefined,
      (price) =>
        getMinAmountByPrice(botAmountData, price, leverage, symbol, exchange),
      gridsData,
      strategyOrdersOptions
    );
  };

  const mergeObjects = (baseObject: any, overrideObject: any) => {
    const allKeys = Object.keys(baseObject).concat(Object.keys(overrideObject));
    allKeys.forEach((objKey) => {
      mergeValues(baseObject, overrideObject, objKey);
    });
  };

  const mergeValues = (baseObject: any, overrideObject: any, objKey: any) => {
    const baseValue = baseObject[objKey];
    const overrideValue = overrideObject[objKey];
    if (!isSymbol(overrideValue)) {
      if (
        !Array.isArray(baseValue) &&
        isObject(baseValue) &&
        isObject(overrideValue)
      ) {
        mergeObjects(baseValue, overrideValue);
      } else {
        baseObject[objKey] = overrideValue;
      }
    }
  };

  const getParsedBotValues = (formValues?: BaseBotFormValues) => {
    let { account, symbols, longShort, multiplier } = formValues || {};
    let isReverse: boolean =
      !isSymbol(longShort) && !!longShort?.includes(LongShort.SHORT);
    return {
      account,
      symbols,
      isReverse,
      multiplier,
    };
  };

  const getBotValidationData = (
    formValues: BaseBotFormValues,
    bot?: BotData
  ) => {
    const botData = bot ?? botGeneralData;
    const { account, symbols, multiplier, isReverse } =
      getParsedBotValues(formValues);
    const {
      defaultStop,
      entryStrategy,
      gridsData,
      hedgingGridsData,
      stopType,
      exchanges,
      useSymbolFromAlert,
      longHedgingRatio,
    } = formValues;
    const symbol = isSymbol(symbols) ? undefined : symbols?.[0];
    const parsedAccount = isSymbol(account) ? undefined : account;
    const parsedExchanges = isSymbol(exchanges) ? bot?.exchanges : exchanges;
    const parsedEntryStrategy = isSymbol(entryStrategy)
      ? undefined
      : entryStrategy;
    const bulkBotGeneralData =
      (parsedAccount?.client_id
        ? botsBulkGeneralData?.[parsedAccount?.client_id]
        : undefined) ??
      bot ??
      botGeneralData;
    const botAmountData = getBotAmountData({
      symbol,
      account: parsedAccount,
      amountValue: isSymbol(formValues.amountValue)
        ? undefined
        : formValues.amountValue,
      amountType: isSymbol(formValues.amountType)
        ? undefined
        : formValues.amountType,
      amountPercentageBaseType: isSymbol(formValues.amountPercentageBaseType)
        ? undefined
        : formValues.amountPercentageBaseType,
      amountCustomBaseValue: formValues.amountCustomBaseValue
        ? parseFloat(formValues.amountCustomBaseValue)
        : undefined,
      editBotData:
        botData && "status" in botData ? (botData as BotData) : undefined,
    });
    const symbolPriceData = getBotCurrentPrice(formValues, bot);
    const currentPrice = symbolPriceData ? parseFloat(symbolPriceData) : 0;
    const pricePrecision = getPrecision(
      PrecisionType.PRICE,
      symbol,
      parsedAccount?.exchange
    );
    const parsedLongHedgingRatio = !isSymbol(longHedgingRatio)
      ? longHedgingRatio
      : bot?.long_exposure_ratio !== undefined
      ? bot.long_exposure_ratio * 100
      : longHedgingRatio;
    const isRangeHedgingBot =
      botType === BotType.HEDGING && isHedgingRangeMode(formValues);
    const entriesEnabled = checkedFields.has(ToggleField.ENTRIES);
    const hedgingOrderOptions: StrategyOrdersOptions | undefined =
      botType === BotType.HEDGING
        ? {
            hedgingGridsData: isRangeHedgingBot
              ? hedgingGridsData
              : undefined,
            longHedgingRatio: parsedLongHedgingRatio,
          }
        : undefined;
    const parsedOrders = getBotParsedEntries(
      botAmountData,
      entryStrategy,
      entriesEnabled,
      currentPrice,
      pricePrecision,
      isSymbol(multiplier) ? undefined : multiplier,
      symbol,
      parsedAccount?.exchange,
      botType === BotType.HEDGING ? false : isReverse,
      undefined,
      hedgingOrderOptions
    );
    const parsedEntries =
      botType === BotType.HEDGING
        ? getHedgingValidationOrders(parsedOrders)
        : parsedOrders;
    const botInitialAmount = getBotInitialAmount(
      parsedEntries,
      botAmountData.mainCoinAmountPrecision || 0,
      botType
    );
    const amountErrors = validateBotAmount(
      parsedEntries,
      botAmountData,
      getBotAmountDataQuery.isFetching,
      isBulkEdit,
      parsedEntryStrategy,
      botInitialAmount,
      currentPrice,
      botType,
      useSymbolFromAlert
    );
    const isFirstEntryValid = validateCustomFirstEntry(parsedEntryStrategy);
    const strategiesValidationData = validateBotStrategies(
      formValues,
      botData,
      currentPrice,
      pricePrecision,
      botType,
      checkedFields
    );
    const isDefaultTakeProfitError = validateDefaultTakeProfit(
      formValues.defaultTakeProfit,
      formValues.longShort
    );

    const isMarginError = validateMarginData(formValues, bulkBotGeneralData);
    const isSymbolValid =
      !isBulkCreate ||
      isSymbolActiveInAccount(symbol, parsedAccount?.client_id);

    const isDefaultStopError =
      defaultStop &&
      !isSymbol(defaultStop) &&
      checkedFields.has(ToggleField.STOP) &&
      validateDefaultStop(
        defaultStop.baseline,
        defaultStop.percent,
        parsedEntries,
        botType,
        isMarginExchange,
        marginType
      );

    const gridsDataIsValid =
      (!isGridBot || validateGridsData(gridsData).isValid) &&
      (botType !== BotType.HEDGING ||
        !isHedgingRangeMode(formValues) ||
        validateHedgingGridsData(hedgingGridsData).isValid);

    const isStopTypeValid = validateBulkStopType(
      stopType,
      botType,
      bulkBotGeneralData
    );

    const isExchangesValid =
      !isTradingViewSignalsBot || isSymbol(exchanges) || !!exchanges?.length;
    const isExchangesValidForSymbol =
      !isTradingViewSignalsBot ||
      useSymbolFromAlert ||
      parsedExchanges?.every((exchange) =>
        isSymbolActiveInExchange(symbol, exchange)
      );
    const isFuturesBot = MARGIN_EXCHANGES.has(bot?.exchanges?.[0] || "");
    const isValidExchangeType =
      !isTradingViewSignalsBot ||
      isSymbol(exchanges) ||
      MARGIN_EXCHANGES.has(exchanges?.[0] || "") === isFuturesBot;

    const isValid =
      isExchangesValid &&
      isValidExchangeType &&
      isExchangesValidForSymbol &&
      !amountErrors.length &&
      isFirstEntryValid &&
      !isDefaultStopError &&
      !isDefaultTakeProfitError &&
      !isMarginError &&
      isSymbolValid &&
      gridsDataIsValid &&
      strategiesValidationData.entriesValid &&
      strategiesValidationData.takeProfitsValid &&
      isStopTypeValid;

    const errorTexts: string[] = [];
    if (!isValidExchangeType) {
      errorTexts.push(
        `This bot is connected to ${
          isFuturesBot ? "Futures" : "Spot"
        } exchanges and cannot be changed to ${
          isFuturesBot ? "Spot" : "Futures"
        } exchanges.`
      );
    }

    const errors: Error[] = [];
    if (isValidExchangeType) {
      if (!isExchangesValid) errors.push(Error.EXCHANGES);
      else if (!isExchangesValidForSymbol)
        errors.push(Error.EXCHANGES_INVALID_FOR_SYMBOL);
      else if (!isSymbolValid) errors.push(Error.INVALID_SYMBOL);
      if (!errors.length) {
        if (amountErrors.length) errors.push(Error.GENERAL_AMOUNT);
        if (isDefaultStopError) errors.push(Error.DEFAULT_STOP);
        if (isDefaultTakeProfitError) errors.push(Error.DEFAULT_TAKE_PROFIT);
        if (isMarginError) errors.push(Error.INVALID_LEVERAGE);
        if (!gridsDataIsValid) errors.push(Error.GRIDS_DATA);
        if (!strategiesValidationData.entriesValid || !isFirstEntryValid)
          errors.push(Error.ENTRY_STRATEGY);
        if (!strategiesValidationData.takeProfitsValid)
          errors.push(Error.TAKE_PROFIT_STRATEGY);
        if (!isStopTypeValid) errors.push(Error.STOP_TYPE);
      }
    }

    return {
      parsedEntries,
      currentPrice,
      isValid,
      parsedData: formValues,
      errors,
      errorTexts,
    };
  };

  const bulkEditBotsData: BulkEditBotData[] = useMemo(() => {
    if (isBulkCreate) return [];
    return (botsData || [])?.map((bot) => {
      const [botValues] = getBotValues(botType, bot, {
        getAccountById,
        pricePrecision: getPrecision(
          PrecisionType.PRICE,
          bot.symbols?.[0],
          bot.exchange
        ),
      });
      const parsedBotData = cloneDeep(botValues);
      Object.keys(parsedBotData).forEach((botKey) => {
        mergeValues(parsedBotData, formValues, botKey);
      });
      return {
        ...bot,
        validationData: getBotValidationData(parsedBotData, bot),
      };
    });
  }, [
    botsData,
    formValues,
    exchangeToSymbolPrices,
    getAccountById,
    getPrecision,
    validateMarginData,
  ]);

  const bulkCreateBotsData: BotBulkActionValidationData[] = useMemo(() => {
    if (!isBulkCreate || isSymbol(accounts) || isSymbol(symbols)) return [];
    const allFormsValues: BotFormValues[] = [];
    accounts?.forEach((account) => {
      const symbol =
        useSymbolFromAlert || !symbols.length
          ? getDefaultSymbol(account.exchange)
          : symbols[0];
      allFormsValues.push({
        ...formValues,
        symbols: symbol ? [symbol] : [],
        account,
        accounts: [account],
      });
    });
    return allFormsValues?.map((botFormValues) => {
      return {
        validationData: getBotValidationData(botFormValues),
      };
    });
  }, [
    accounts,
    symbols,
    formValues,
    exchangeToSymbolPrices,
    getAccountById,
    isSuccess,
    validateMarginData,
  ]);

  const bulkActionBotsData: BotBulkActionData[] = isBulkCreate
    ? bulkCreateBotsData
    : bulkEditBotsData;
  const [bulkActionValidBotsData, bulkActionInvalidBotsData] = useMemo(() => {
    let validBots: BotBulkActionData[] = [];
    let invalidBots: BotBulkActionData[] = [];
    bulkActionBotsData?.forEach((bot) => {
      if (bot.validationData.isValid) validBots.push(bot);
      else invalidBots.push(bot);
    });
    return [validBots, invalidBots];
  }, [bulkActionBotsData]);

  return {
    bulkActionBotsData,
    bulkActionValidBotsData,
    bulkActionInvalidBotsData,
  };
};

export default useValidateBots;
